import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog"
import { Button } from "../ui/button"
import { cn } from "../../lib/utils"
import { useEncounter } from "../../contexts/EncounterContext"
import type { NoteType } from "../../contexts/EncounterContext"
import {
  FileText,
  ClipboardList,
  Stethoscope,
  ScrollText,
  LogOut,
  MessageSquare,
  Scissors,
  ArrowRightLeft,
  Mic,
  ShieldCheck,
} from "lucide-react"

interface ConsentModalProps {
  open: boolean
  onClose: () => void
}

const NOTE_TYPES: {
  type: NoteType
  label: string
  description: string
  icon: React.ElementType
}[] = [
  { type: "soap", label: "SOAP Note", description: "Subjective, Objective, Assessment, Plan", icon: FileText },
  { type: "hpi", label: "HPI", description: "History of Present Illness", icon: ScrollText },
  { type: "ap", label: "A&P", description: "Assessment & Plan", icon: ClipboardList },
  { type: "hp", label: "H&P", description: "History & Physical", icon: Stethoscope },
  { type: "discharge", label: "Discharge Summary", description: "Discharge documentation", icon: LogOut },
  { type: "consult", label: "Consultation Note", description: "Specialist consultation", icon: MessageSquare },
  { type: "procedure", label: "Procedure Note", description: "Procedural documentation", icon: Scissors },
  { type: "ipass", label: "I-PASS Handoff", description: "Standardized handoff", icon: ArrowRightLeft },
]

const ConsentModal = ({ open, onClose }: ConsentModalProps) => {
  const { startEncounter, recordConsent, error } = useEncounter()
  const [selectedType, setSelectedType] = useState<NoteType | null>(null)
  const [consentChecked, setConsentChecked] = useState(false)
  const [loading, setLoading] = useState(false)

  const canProceed = selectedType !== null && consentChecked

  const handleBegin = async () => {
    if (!selectedType) return
    setLoading(true)
    try {
      startEncounter(selectedType)
      await recordConsent("electronic")
      // Modal closes automatically because status transitions to 'recording'
      // which makes open={encounterStatus === 'consent'} become false
    } catch (err) {
      // Defensive: recordConsent handles errors internally, but catch here in case that changes
      console.error('Failed to start encounter:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setSelectedType(null)
    setConsentChecked(false)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Mic className="h-5 w-5 text-blue-500" />
            Ambient Scribe
          </DialogTitle>
          <DialogDescription>
            Select a note type and provide consent to begin recording.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-4">
          {NOTE_TYPES.map(({ type, label, description, icon: Icon }) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={cn(
                "flex flex-col items-center gap-2 rounded-lg border p-3 text-center transition-all hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950",
                selectedType === type
                  ? "border-blue-500 bg-blue-50 ring-2 ring-blue-500/30 dark:bg-blue-950"
                  : "border-border"
              )}
            >
              <Icon className={cn("h-6 w-6", selectedType === type ? "text-blue-500" : "text-muted-foreground")} />
              <span className="text-sm font-medium leading-tight">{label}</span>
              <span className="text-xs text-muted-foreground leading-tight">{description}</span>
            </button>
          ))}
        </div>

        <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground space-y-2">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <ShieldCheck className="h-4 w-4 text-green-600" />
            HIPAA Disclosure
          </div>
          <p>
            This encounter will be audio-recorded and transcribed using AI. The recording
            is processed in real-time and is not stored after transcription. The generated
            clinical note will be saved to the patient chart. All data is encrypted in
            transit and at rest in compliance with HIPAA regulations.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <label className="flex items-start gap-3 cursor-pointer py-2 min-h-[48px]">
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => setConsentChecked(e.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 shrink-0"
          />
          <span className="text-sm">
            I confirm that patient consent has been obtained for AI-assisted documentation
            of this encounter.
          </span>
        </label>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button disabled={!canProceed || loading} onClick={handleBegin} className="w-full sm:w-auto">
            {loading ? "Starting..." : "Begin Recording"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConsentModal
